import { Component, Input, OnChanges, ChangeDetectionStrategy } from '@angular/core';
import { TeamsService, MonthlyTopupSchedule, MonthlyTopupSchedulePreview } from '../../services/teams/teams.service';

@Component({
    selector: 'app-monthly-topup-schedule',
    templateUrl: './monthly-topup-schedule.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MonthlyTopupScheduleComponent implements OnChanges {
  @Input() teamId?: string;
  @Input() disabled = false;
  // Recheck actual billing after the parent accepts updated team settings.
  @Input() settingsVersion = '';
  private loadVersion = 0;
  readonly days = Array.from({ length: 28 }, (_, i) => i + 1);
  day?: number;
  currentDay?: number;
  nextFundingDate: string | null = null;
  hasSubscription = false;
  fundingStatus = '';
  fundingAttentionReason: MonthlyTopupSchedule['fundingAttentionReason'] = null;
  fundingActionsEnabled = false;
  configuredMonthlyAmount = 0;
  recoveryPreview: any = null;
  pending = false;
  loading = false;
  error = '';
  message = '';
  preview: MonthlyTopupSchedulePreview | null = null;

  get hasUnsavedDay(): boolean {
    return this.day !== undefined && this.day !== this.currentDay;
  }

  get fundingBlocked(): boolean {
    return this.loading || !!this.error || this.pending || this.hasUnsavedDay;
  }

  constructor(private readonly teams: TeamsService) {}

  ngOnChanges(): void {
    void this.load();
  }

  async load(): Promise<void> {
    const teamId = this.teamId;
    const version = ++this.loadVersion;
    this.preview = null;
    this.recoveryPreview = null;
    this.fundingActionsEnabled = false;
    this.fundingAttentionReason = null;
    this.error = '';
    this.message = '';
    if (!teamId) return;
    this.loading = true;
    try {
      const response = await this.teams.getMonthlyTopupSchedule(teamId);
      if (teamId !== this.teamId || version !== this.loadVersion) return;
      if (!response.success) throw new Error(response.message || 'Unable to load schedule.');
      this.currentDay = response.data.monthlyTopupDay;
      this.day = response.data.pendingDay ?? this.currentDay;
      this.nextFundingDate = response.data.nextFundingDate;
      this.hasSubscription = response.data.hasSubscription;
      this.fundingStatus = response.data.fundingStatus || '';
      this.fundingAttentionReason = response.data.fundingAttentionReason || null;
      this.fundingActionsEnabled = response.data.fundingActionsEnabled === true;
      this.configuredMonthlyAmount = response.data.configuredMonthlyAmount || 0;
      this.pending = response.data.pending;
    } catch (error: any) {
      if (teamId === this.teamId && version === this.loadVersion) this.error = this.errorMessage(error);
    } finally {
      if (teamId === this.teamId && version === this.loadVersion) this.loading = false;
    }
  }

  async review(): Promise<void> {
    const teamId = this.teamId;
    if (!teamId || !this.day) return;
    this.loading = true;
    this.error = '';
    this.message = '';
    try {
      const response = await this.teams.previewMonthlyTopupSchedule(teamId, this.day);
      if (teamId !== this.teamId) return;
      if (!response.success) throw new Error(response.message || 'Unable to preview schedule.');
      this.preview = response.data;
    } catch (error: any) {
      if (teamId === this.teamId) this.error = this.errorMessage(error);
    } finally {
      if (teamId === this.teamId) this.loading = false;
    }
  }

  async confirm(): Promise<void> {
    const teamId = this.teamId;
    const preview = this.preview;
    if (!preview || !teamId) return;
    this.loading = true;
    this.error = '';
    try {
      const response = await this.teams.changeMonthlyTopupSchedule(teamId, preview.monthlyTopupDay, preview.quoteToken);
      if (teamId !== this.teamId) return;
      if (!response.success) throw new Error(response.message || 'Unable to change schedule.');
      await this.load();
      this.message = 'Monthly top-up day saved.';
    } catch (error: any) {
      if (teamId === this.teamId) this.error = this.errorMessage(error);
    } finally {
      if (teamId === this.teamId) this.loading = false;
    }
  }

  get fundingStatusLabel(): string {
    return ({scheduled: 'Scheduled', paused: 'Paused', disabled: 'Disabled',
      setup_required: 'Setup incomplete', recovery_required: 'Recurring funding missing',
      recovery_pending: 'Funding recovery pending', attention_required: 'Funding needs review', out_of_sync: 'Funding amount needs updating'} as Record<string, string>)[this.fundingStatus] || 'Checking funding status';
  }

  get fundingAttentionMessage(): string {
    switch (this.fundingAttentionReason) {
      case 'recovery_failed':
        return 'The recovery attempt did not complete. Contact support to reconcile that attempt before starting another, so the team is not billed twice.';
      case 'subscription_canceled':
        return 'The existing allowance subscription is canceled or scheduled to end. If you want to resume funding, contact support to reconcile the existing billing before it restarts.';
      case 'subscription_paused':
        return 'The existing allowance subscription is paused in billing. If you want to resume funding, contact support to reconcile the paused subscription before it restarts.';
      case 'payment_unresolved':
        return 'An allowance payment is unresolved. Check the team’s default funding method and complete any required verification, then reload funding status. If the payment remains unresolved, contact support to reconcile the unpaid billing.';
      case 'subscription_review':
        return 'The existing allowance subscription cannot fund normally. Contact support to reconcile its billing state before restarting funding.';
      default:
        return 'Reload funding status to check what needs attention before retrying. Your saved team settings are unchanged.';
    }
  }

  async reviewRecovery(): Promise<void> {
    const teamId = this.teamId;
    if (!teamId || !this.fundingActionsEnabled || this.disabled || this.loading || this.hasUnsavedDay) return;
    this.loading = true;
    this.error = '';
    try {
      const response = await this.teams.previewAllowanceRecovery(teamId);
      if (teamId !== this.teamId) return;
      if (!response.success) throw new Error(response.message);
      this.recoveryPreview = response.data;
    } catch (error: any) {
      if (teamId === this.teamId) this.error = this.errorMessage(error);
    } finally {
      if (teamId === this.teamId) this.loading = false;
    }
  }

  async confirmRecovery(): Promise<void> {
    const teamId = this.teamId;
    const quote = this.recoveryPreview;
    if (!teamId || !quote || !this.fundingActionsEnabled || this.disabled || this.loading) return;
    this.loading = true;
    this.error = '';
    try {
      const response = await this.teams.recoverAllowance(teamId, quote.quoteToken);
      if (teamId !== this.teamId) return;
      if (!response.success) throw new Error(response.message);
      await this.load();
      this.message = response.data.status === 'COMPLETED'
        ? 'Recurring funding restored for the next scheduled date. No catch-up funding was requested.'
        : 'Recovery has not completed. Reload to check the outcome; do not start a second recovery.';
    } catch (error: any) {
      if (teamId === this.teamId) this.error = this.errorMessage(error);
    } finally {
      if (teamId === this.teamId) this.loading = false;
    }
  }

  async synchronize(): Promise<void> {
    const teamId = this.teamId;
    if (!teamId || !this.fundingActionsEnabled || this.disabled || this.loading) return;
    this.loading = true;
    this.error = '';
    try {
      const response = await this.teams.synchronizeAllowance(teamId);
      if (teamId !== this.teamId) return;
      if (!response.success) throw new Error(response.message);
      await this.load();
      this.message = response.data.message;
    } catch (error: any) {
      if (teamId === this.teamId) this.error = this.errorMessage(error);
    } finally {
      if (teamId === this.teamId) this.loading = false;
    }
  }

  private errorMessage(error: any): string {
    const body = error?.data || error?.error || error;
    const reason = body?.details?.reason || body?.reason || body?.code;
    if (reason === 'QUOTE_STALE') return 'The preview is out of date. Reload funding status, then review and confirm a new preview.';
    if (reason === 'ALLOWANCE_BILLING_BUSY') return 'Another funding action is in progress. Wait for it to finish, then reload funding status before retrying.';
    if (reason === 'RECOVERY_PENDING') return 'A recovery attempt already exists. Reload funding status to see whether it is still processing or needs billing reconciliation.';
    if (reason === 'ALLOWANCE_ALREADY_SCHEDULED') return 'Recurring funding already exists. Reload funding status to view it before taking another action.';
    if (reason === 'ALLOWANCE_SUBSCRIPTION_IDENTITY_CONFLICT') return 'The existing allowance subscription could not be identified safely. Contact support to reconcile the team’s billing before retrying.';
    if (reason === 'ALLOWANCE_ROLLOUT_DISABLED') return 'Funding recovery is not enabled for this team. You can continue editing team settings using the existing controls. No new recovery was started.';
    return body?.message || error?.message || 'Unable to update the schedule. Reload to check the change before retrying.';
  }
}
